import React, { useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { Empty } from "./ui";
import { Plus, Pencil, Trash2, Check, ChevronUp, ChevronDown, ChevronRight } from "lucide-react";

// fields: [{name, label, type: text|textarea|select, options?:[{value,label}]|[string], full?:bool, group?:"internal"}]
// includeToggle: show an "Include in Blueprint" control per item.
// includeReady(item): returns true when the item is eligible to be included (final status).
export default function RecordManager({
  resource, bid, items, fields, title, addLabel = "Add", onChange,
  renderItem, testid, includeToggle = false, includeReady,
  reorderable = false, onReorder, itemActions,
}) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [showInternal, setShowInternal] = useState(false);

  const internalFields = (fields || []).filter((f) => f.group === "internal");
  const primaryFields = (fields || []).filter((f) => f.group !== "internal");

  const openNew = () => { setEditing(null); setForm({}); setShowInternal(false); setOpen(true); };
  const openEdit = (it) => {
    setEditing(it); setForm({ ...it });
    setShowInternal(internalFields.some((f) => it[f.name] != null && it[f.name] !== ""));
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) await api.patch(`/${resource}/${editing.id}`, form);
      else await api.post(`/businesses/${bid}/${resource}`, form);
      setOpen(false);
      toast.success(editing ? "Updated" : "Added");
      onChange && onChange();
    } catch (e) {
      toast.error("Could not save");
    } finally { setSaving(false); }
  };

  const remove = async (it) => {
    if (!window.confirm("Delete this record?")) return;
    try { await api.delete(`/${resource}/${it.id}`); onChange && onChange(); toast.success("Deleted"); }
    catch { toast.error("Could not delete"); }
  };

  const toggleInclude = async (it) => {
    const next = !it.include_in_blueprint;
    try {
      await api.patch(`/${resource}/${it.id}`, { include_in_blueprint: next });
      onChange && onChange();
      toast.success(next ? "Added to Blueprint" : "Removed from Blueprint");
    } catch { toast.error("Could not update"); }
  };

  const opt = (o) => (typeof o === "string" ? { value: o, label: o } : o);

  const renderField = (f) => (
    <div key={f.name}>
      <Label className="text-lr-charcoal">{f.label}</Label>
      {f.type === "textarea" ? (
        <Textarea className="mt-1 bg-white/60" rows={f.rows || 3} value={form[f.name] || ""}
          onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} data-testid={`${testid}-field-${f.name}`} />
      ) : f.type === "select" ? (
        <Select value={form[f.name] || ""} onValueChange={(v) => setForm({ ...form, [f.name]: v })}>
          <SelectTrigger className="mt-1 bg-white/60" data-testid={`${testid}-field-${f.name}`}><SelectValue placeholder="Select…" /></SelectTrigger>
          <SelectContent>{f.options.map(opt).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
        </Select>
      ) : (
        <Input className="mt-1 bg-white/60" value={form[f.name] || ""}
          onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} data-testid={`${testid}-field-${f.name}`} />
      )}
    </div>
  );

  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-serif text-xl text-lr-charcoal">{title}</h3>
        <Button size="sm" onClick={openNew} data-testid={`${testid}-add`}
          className="bg-lr-olive hover:bg-[#3d4230] text-lr-cream rounded-[4px]">
          <Plus className="h-4 w-4 mr-1" /> {addLabel}
        </Button>
      </div>

      {(!items || items.length === 0) ? (
        <Empty>Nothing here yet.</Empty>
      ) : (
        <div className="space-y-3">
          {items.map((it, idx) => {
            const eligible = !includeReady || includeReady(it);
            const on = !!it.include_in_blueprint;
            return (
            <div key={it.id} className="lr-card p-4 group" data-testid={`${testid}-item`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  {renderItem ? renderItem(it) : (
                    <div className="space-y-1">
                      {fields.map((f) => it[f.name] != null && it[f.name] !== "" && (
                        <div key={f.name} className="text-sm">
                          <span className="text-lr-leather">{f.label}: </span>
                          <span className="text-lr-charcoal">{Array.isArray(it[f.name]) ? it[f.name].join(", ") : it[f.name]}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-start gap-1 flex-shrink-0">
                  {itemActions && itemActions(it)}
                  {reorderable && (
                    <div className="flex flex-col mr-1" data-testid={`${testid}-reorder`}>
                      <button disabled={idx === 0} onClick={() => onReorder && onReorder(idx, -1)} data-testid={`${testid}-move-up`}
                        className="p-0.5 text-lr-leather hover:text-lr-olive disabled:opacity-25 disabled:cursor-not-allowed" title="Move up"><ChevronUp className="h-4 w-4" /></button>
                      <button disabled={idx === items.length - 1} onClick={() => onReorder && onReorder(idx, 1)} data-testid={`${testid}-move-down`}
                        className="p-0.5 text-lr-leather hover:text-lr-olive disabled:opacity-25 disabled:cursor-not-allowed" title="Move down"><ChevronDown className="h-4 w-4" /></button>
                    </div>
                  )}
                  <button onClick={() => openEdit(it)} data-testid={`${testid}-edit`} className="p-1.5 text-lr-leather hover:text-lr-olive"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => remove(it)} data-testid={`${testid}-delete`} className="p-1.5 text-lr-leather hover:text-lr-rose"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              {includeToggle && (
                <div className="mt-3 pt-3 border-t hairline flex items-center justify-between">
                  {eligible ? (
                    <button
                      onClick={() => toggleInclude(it)}
                      data-testid={`${testid}-include`}
                      className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-[4px] border transition-colors ${on ? "bg-lr-olive text-lr-cream border-lr-olive" : "border-lr-leather/30 text-lr-leather hover:border-lr-olive"}`}>
                      <Check className="h-3.5 w-3.5" /> {on ? "In Blueprint" : "Include in Blueprint"}
                    </button>
                  ) : (
                    <span className="text-xs text-lr-leather/70 italic" data-testid={`${testid}-include-locked`}>
                      Mark this record final before it can appear in the Blueprint
                    </span>
                  )}
                </div>
              )}
            </div>
          );})}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-lr-card max-h-[85vh] overflow-y-auto lr-scroll">
          <DialogHeader><DialogTitle className="font-serif">{editing ? "Edit" : addLabel}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            {primaryFields.map((f) => renderField(f))}
            {internalFields.length > 0 && (
              <div className="pt-3 border-t hairline">
                <button type="button" onClick={() => setShowInternal((v) => !v)} data-testid={`${testid}-toggle-internal`}
                  className="flex items-center gap-1.5 text-sm text-lr-olive font-medium hover:text-[#3d4230]">
                  {showInternal ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />} Internal analysis
                </button>
                {showInternal && <div className="space-y-4 mt-3">{internalFields.map((f) => renderField(f))}</div>}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-[4px]">Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid={`${testid}-save`} className="bg-lr-olive hover:bg-[#3d4230] text-lr-cream rounded-[4px]">
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
